import { Button, SplitButton, Uploader, UploadStatus } from "@lyfegen-platform/react-common";
import { format } from "date-fns";
import saveAs from "file-saver";
import { pick } from "lodash";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useDataMutation, useDataQuery, useLazyDataQuery } from "../../../../hooks/useDataModule";
import { AgreementRunStatus, FlexContainerType, FlexEntityUploadReport } from "../../../../models/generated/graphql";
import { agreementExecutionTotalCount, agreementsExecution } from "../../../../store/agreements";
import { isDataCreationDisabled } from "../../../../store/data";
import { userOrganization } from "../../../../store/layout";
import { upload } from "../../../../store/upload";
import getReportFileData from "./get-report-txt";
import {
	CANCEL_UPLOAD,
	GET_ENTITY_NAME,
	GET_UPLOAD_REPORT,
	GET_UPLOAD_STATE,
	START_FINAL_UPLOAD,
	START_PRELIMINARY_UPLOAD,
} from "./queries";
import {
	GetEntityNameQuery,
	GetUploadReportQuery,
	GetUploadStateQuery,
	StartFinalUploadMutation,
	StartPreliminaryUploadMutation,
} from "./types/queries.generated";

const Root = () => {
	const { t } = useTranslation();
	const [
		{
			status,
			flexEntityTypeId,
			file,
			mappedFields,
			sourceKeys,
			report,
			error,
			progress,
			isDisplayed,
			saveMapping,
			duplicatesFromFile,
			totalNumberOfRecords,
			expectedRunAgreements,
			completedAgreementIds,
		},
		setUploadState,
	] = useRecoilState(upload);
	const setAgreementsExecutionState = useSetRecoilState(agreementsExecution);
	const setAgreementsExecutionTotalCountValue = useSetRecoilState(agreementExecutionTotalCount);
	const setIsDataCreationDisabled = useSetRecoilState(isDataCreationDisabled);
	const [getEntityName, { data: entityName }] = useLazyDataQuery<GetEntityNameQuery>(GET_ENTITY_NAME);
	const [cancelUpload] = useDataMutation(CANCEL_UPLOAD);
	const isComplete = status === UploadStatus.AgreementExecution && progress === 100;

	useDataQuery<GetUploadStateQuery>(GET_UPLOAD_STATE, {
		fetchPolicy: "no-cache",
		onCompleted: (data) => {
			if (data.GetUploadState) {
				setUploadState(() => ({
					type: FlexContainerType.Form,
					rememberMapping: false,
					isDisplayed: true,
					...data.GetUploadState!,
					progress: data.GetUploadState!.percentage,
				}));
			}
		},
	});
	const [startPreliminaryUpload] = useDataMutation<StartPreliminaryUploadMutation>(START_PRELIMINARY_UPLOAD, {
		fetchPolicy: "no-cache",
	});
	const [startFinalUpload] = useDataMutation<StartFinalUploadMutation>(START_FINAL_UPLOAD, { fetchPolicy: "no-cache" });
	const [getReport] = useLazyDataQuery<GetUploadReportQuery>(GET_UPLOAD_REPORT, {
		fetchPolicy: "no-cache",
		onCompleted: (data) => setUploadState((prev) => ({ ...prev, report: data.GetUploadReport as FlexEntityUploadReport })),
	});

	const { name: userOrgName } = useRecoilValue(userOrganization);
	const fileName = `Data upload report for ${userOrgName} on ${format(new Date(), "yyyy-MM-dd")}.txt`;
	const fileType = "text/plain;charset=utf-8";
	const reportTxtProps = {
		report,
		userOrgName,
		t,
		formType: entityName?.flexEntityTypes[0]?.localizationKey || "",
		isPreliminary: status === UploadStatus.PreliminaryReport,
		uploadType: entityName?.flexEntityTypes[0]?.containerType || FlexContainerType.Form,
		duplicatesFromFile: duplicatesFromFile || 0,
		totalNumberOfRecords: totalNumberOfRecords || 0,
	};

	const handleClose = () => {
		cancelUpload();
		//!	evict subscription cache to not display on other subscription updates.
	};

	const handleUpload = () => {
		startFinalUpload({
			variables: {
				input: {
					flexEntityTypeId,
					file,
					mappedFields: mappedFields.map((field) => pick(field, "userField", "flexEntityField", "derivedFrom")),
					sourceKeys,
				},
			},
		});
	};

	const handleGetFinalReport = () => {
		const data = new Blob([getReportFileData({ ...reportTxtProps, isPreliminary: false })], { type: fileType });
		saveAs(data, fileName);
	};

	const handleGetPreliminaryReport = () => {
		const data = new Blob([getReportFileData(reportTxtProps)], { type: fileType });
		saveAs(data, fileName);
	};

	const uploadAndGetReport = () => {
		handleUpload();
		handleGetFinalReport();
	};

	const getComponentButtons = (): Array<JSX.Element> => {
		if (status === UploadStatus.PreliminaryReport)
			return [
				<Button color="primary" onClick={handleClose} use="fab">
					{t("cancel")}
				</Button>,
				<SplitButton
					mainButtonStyles={{ fontSize: "12px", whiteSpace: "nowrap" }}
					options={[
						{ label: t("upload-and-report"), isMainButton: true, onClick: uploadAndGetReport },
						{ label: t("upload"), onClick: handleUpload },
						{ label: t("get-report"), onClick: handleGetPreliminaryReport },
					]}
				/>,
			];

		if (status === UploadStatus.FinalReport || status === UploadStatus.AgreementExecution)
			return [
				<Button color="primary" use="secondary" onClick={handleGetFinalReport}>
					{t("get-report")}
				</Button>,
			];

		return [];
	};

	useEffect(() => {
		if (status && status === UploadStatus.Inspecting) {
			startPreliminaryUpload({
				variables: {
					input: {
						flexEntityTypeId,
						file,
						mappedFields: mappedFields.map((field) => pick(field, "userField", "flexEntityField", "derivedFrom")),
						sourceKeys,
						saveMapping,
					},
				},
			});
		}

		getEntityName({ variables: { entityId: flexEntityTypeId } });
	}, [status]);

	useEffect(() => {
		if ((status && [UploadStatus.PreliminaryReport, UploadStatus.FinalReport].includes(status)) || isComplete) {
			getReport();
		}
	}, [status, progress]);

	useEffect(() => {
		setUploadState((prev) => ({
			...prev,
			type: reportTxtProps.uploadType,
		}));
	}, [reportTxtProps.uploadType]);

	useEffect(() => {
		if (status === UploadStatus.PreliminaryReport || status === UploadStatus.FinalReport) {
			setUploadState((prev) => ({
				...prev,
				isDisplayed: true,
			}));
		}
		if (status === UploadStatus.AgreementExecution) {
			setUploadState((prev) => ({
				...prev,
				isDisplayed: isComplete,
			}));
		}
		if (status === UploadStatus.Completing) {
			setUploadState((prev) => ({
				...prev,
				isDisplayed: false,
			}));
		}
	}, [status, progress]);

	useEffect(() => {
		if (expectedRunAgreements && completedAgreementIds) {
			setAgreementsExecutionState(
				expectedRunAgreements.map(({ id, name }) => ({
					id,
					name,
					status: completedAgreementIds.includes(id) ? AgreementRunStatus.Executed : AgreementRunStatus.InExecution,
					tenantIds: [],
					isAgreementRunSubscriptionActive: true,
				}))
			);
			setAgreementsExecutionTotalCountValue(expectedRunAgreements.map((a) => a.id));
			setIsDataCreationDisabled(
				(status && [UploadStatus.Inspecting, UploadStatus.Uploading].includes(status)) ||
					(status === UploadStatus.AgreementExecution && (!progress || progress < 100))
			);
		}
	}, [status, expectedRunAgreements, completedAgreementIds, progress]);

	if (!isDisplayed) {
		return <></>;
	}

	const uploaderStatus = isComplete ? UploadStatus.FinalReport : status;

	return (
		<Uploader
			status={uploaderStatus}
			report={report}
			progress={progress}
			cancel={handleClose}
			error={error}
			t={t}
			buttons={getComponentButtons()}
			type={reportTxtProps.uploadType as any}
		/>
	);
};

export default Root;
